import { useState, useEffect } from 'react'
import { API_BASE_URL } from 'config/api'
import { ProductImage } from 'utils/imageUtils'

interface Product {
  id: string
  title: string
  price: number
  compareAtPrice: number | null
  images: ProductImage[]
  isActive: boolean
  isAvailableForSale: boolean
  attributes: {
    author: string
    appearance: string
    pagesNumber: string
    publishYear: string
    translator: string
  }
  categoryTitle?: string
  vendor?: string
}

/**
 * Hook to fetch product details for multiple product IDs
 * Useful for guest users who only have product IDs in localStorage
 */
export function useWishlistProducts(productIds: string[]) {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (productIds.length === 0) {
      setProducts([])
      setLoading(false)
      return
    }

    const fetchProducts = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch each product individually
        // In production, you might want to create a batch endpoint
        const promises = productIds.map((id) =>
          fetch(`${API_BASE_URL}/products/${id}`)
            .then((res) => {
              if (!res.ok) throw new Error(`Failed to fetch product ${id}`)
              return res.json()
            })
            .catch((err) => {
              console.error(`Error fetching product ${id}:`, err)
              return null
            }),
        )

        const results = await Promise.all(promises)
        const validProducts = results.filter((product) => product !== null)
        setProducts(validProducts)
      } catch (err) {
        console.error('Error fetching wishlist products:', err)
        setError('خطا در دریافت محصولات')
      } finally {
        setLoading(false)
      }
    }

    fetchProducts()
  }, [productIds.join(',')]) // Use join to avoid unnecessary re-fetches

  return {
    products,
    loading,
    error,
  }
}

